<template>
  <div
    v-if="needToSelectCarrier || errorType !== null || errorMessage !== null"
    class="about"
  >
    <p v-if="needToSelectCarrier">
      Does dispatcher need to select a carrier? {{ needToSelectCarrier }}
    </p>
    <ErrorMessage v-else-if="missingOnboardingScope">
      <p class="text-justify">
        Your account does not have the necessary permissions to complete
        onboarding. Please contact your internal administrator to request access
        to the onboarding scope.
      </p>
    </ErrorMessage>

    <p v-else>{{ errorType }} - {{ errorMessage }}</p>
  </div>
  <div v-else>
    <loading />
  </div>
</template>
<script>
import ErrorMessage from '@/components/ErrorMessage.vue';
import { toastResponse } from '@/common/handleServerResponse';
import Loading from '@/components/Loading.vue';

export default {
  name: 'OAuth Authorization',
  components: { Loading, ErrorMessage },
  data() {
    return {
      errorType: null,
      errorMessage: null,
    };
  },
  computed: {
    missingOnboardingScope() {
      return (
        (this.errorType === 'access_denied' &&
          this.errorMessage?.includes('Requires a user to have onboarding')) ||
        false
      );
    },
    currentUser() {
      return this.$store.state.users.currentUser;
    },
    companyType() {
      return this.$store.state.users.currentUser?.company?.company_type;
    },
    dispatcherSelectedCarrierId() {
      return this.$store.state.dispatchers.selectedCarrierId;
    },
    needToSelectCarrier() {
      return (
        this.companyType == 'dispatcher' &&
        this.dispatcherSelectedCarrierId == null
      );
    },
    // OAuth params
    clientId() {
      return this.$route.query.client_id;
    },
    scope() {
      return this.$route.query.scope;
    },
    state() {
      return this.$route.query.state;
    },
    redirect_uri() {
      return this.$route.query.redirect_uri;
    },
    response_type() {
      return this.$route.query.response_type;
    },
  },
  async created() {
    console.log('loading oauth/authorizations');
    await this.$store.dispatch('users/serverRefreshCurrentUser');

    // If they aren't signed in, then send them to sign in and set the return back to this page
    if (this.currentUser == null) {
      localStorage.setItem(
        'redirect_url_after_sigin',
        JSON.stringify({
          path: '/oauth/authorizations',
          params: this.$route.query,
        })
      );
      return this.$router.push({ name: 'SignUp' });
    }

    // Based on the companyType, we want to process
    // the pre-loading differently
    switch (this.companyType) {
      case 'carrier':
        return await this.processForCarrier();
      case 'broker':
        return await this.processForCarrier();
      case 'dispatcher':
        return await this.processForDispatcher();
    }
  },
  methods: {
    // Process an OAuth 2 link click by a dispatcher
    async processForDispatcher() {
      console.log('processing dispatcher on oauth/authorizations');
      // If they haven't selected a carrier, then they
      // need to do that on this screen
      if (this.dispatcherSelectedCarrierId == null) {
        console.log('Go to select carrier view');
        // localStorage.setItem(
        //   "redirect_url_after_sigin",
        //   JSON.stringify({
        //     path: "/oauth/authorizations",
        //     params: this.$route.query,
        //   })
        return this.$router.push({
          name: 'SelectCarrier',
          query: this.$route.query,
        });
      }

      // If Selected Carrier is present, then make a call to find or create
      // onboarding packet and move forward
      let body = {
        carrier_id: this.dispatcherSelectedCarrierId,
        client_id: this.clientId,
      };
      let res = await this.$store.dispatch(
        'onboardingPacket/createDispatcherOnBoardingPacket',
        body
      );

      switch (res.status) {
        case 200:
          await this.$store.dispatch(
            'authorizations/setConnectionInfo',
            res.data
          );
          console.log('routing to the onboarding packet');
          // remove redirect URL because when you finish the
          // packet, the dispatcher is going to be taken to a status
          // screen
          localStorage.removeItem('redirect_url_after_sigin');
          return this.$router.push({
            name: 'Complete Holding Tank',
          });
        case 201:
          await this.$store.dispatch(
            'authorizations/setConnectionInfo',
            res.data
          );
          console.log('routing to the onboarding packet');
          // remove redirect URL because when you finish the
          // packet, the dispatcher is going to be taken to a status
          // screen
          localStorage.removeItem('redirect_url_after_sigin');
          return this.$router.push({
            name: 'Dispatcher Onboarding Packet',
            params: { id: res.data.id },
          });
        case 400:
          // this happens when the Oauth link is malformed
          console.log('400 response creating onboarding packet');
          // remove the redirect URL because they are not going to be
          // able to proceed
          localStorage.removeItem('redirect_url_after_sigin');
          this.errorType = res.data.error;
          this.errorMessage = res.data.error_description;
          // TODO: WE NEED TO HANDLE THIS CASE BY SHOWING THE USER SOME UI;
          // YOU CAN TEST BY ADDING A BAD PARAM IN THE OAUTH2 LINK ON THE LOGIN
          // BUTTON
          // EXAMPLE LINK: https://staging.highway.com/onboarding/oauth/authorizations?response_type=code&client_id=3542d087-57d8-44a8-aefb-c581b8e86a08&scope=all&redirect_uri=https%3A%2F%2Fstaging-login-screens.s3.amazonaws.com%2Fportals%2Farrive.html
          break;
        case 422:
          console.log(
            '422 response for a dispatcher trying to onboard a carrier'
          );
          toastResponse(this, res, null);
          return this.$router.push({
            name: 'Dispatcher Dashboard',
          });
        case 423:
          console.log(
            '423 response creating onboarding packet; Do Not Dispatch'
          );
          // remove the redirect URL because they are not going to be
          // able to proceed
          localStorage.removeItem('redirect_url_after_sigin');
          return this.$router.push({
            name: 'Dispatcher Do Not Dispatch Holding Tank',
          });
        default:
          // remove the redirect URL because they are not going to be
          // able to proceed.
          localStorage.removeItem('redirect_url_after_sigin');
          throw (
            res.status.toString() +
            ' - Response from oauth/authorizations not supported'
          );
      }
    },
    // Process an OAuth 2 link click by a carrier
    async processForCarrier() {
      console.log('processing carrier createAuthorization');

      let res = await this.$store.dispatch(
        'authorizations/createAuthorization'
      );

      switch (res.status) {
        case 200:
          // Need to send the scopes to the server
          break;
        case 201:
          // User is onboarded -- redirect to redirectUri
          // remove the redirect url because they have successfully
          // completed
          localStorage.removeItem('redirect_url_after_sigin');
          window.location.href = res.data.redirect_uri;
          return;
        case 422:
          // User needs to onboard, send to process the onboarding packet
          // Set the redirect URL because they are going to need to
          // fill out the onboarding packet and come back here
          console.log('422 response creating onboarding packet', res?.data);
          localStorage.setItem(
            'redirect_url_after_sigin',
            JSON.stringify({
              path: '/oauth/authorizations',
              params: this.$route.query,
            })
          );
          if (res?.data?.onboarding_packet_id) {
            return this.$router.push({
              name: 'Carrier Onboarding Packet',
              params: { id: res.data.onboarding_packet_id },
            });
          } else if (this.$store.state.onboardingPacket?.onboarding_id > 0) {
            // Looking for onboarding_packet id in the store
            this.$rollbar.error(
              '422 response creating onboarding packet; state onboarding_id found; redirected to packet',
              {
                customData: {
                  onboarding_packet_id:
                    this.$store.state.onboardingPacket?.onboarding_id,
                  message: res?.data?.message,
                  res: res,
                  data: res?.data,
                  status: res?.status,
                },
              }
            );
            return this.$router.push({
              name: 'Carrier Onboarding Packet',
              params: { id: this.$store.state.onboardingPacket.onboarding_id },
            });
          } else if (this.$store.state.onboardingPacket?.id > 0) {
            // Looking for onboarding_packet id again in the store
            this.$rollbar.error(
              '422 response creating onboarding packet; state id found; redirected to packet',
              {
                customData: {
                  onboarding_packet_id: this.$store.state.onboardingPacket?.id,
                  message: res?.data?.message,
                  res: res,
                  data: res?.data,
                  status: res?.status,
                },
              }
            );
            return this.$router.push({
              name: 'Carrier Onboarding Packet',
              params: { id: this.$store.state.onboardingPacket.id },
            });
          } else {
            this.$rollbar.error(
              '422 response creating onboarding packet; NO PACKET ID found; redirected to carrier dashboard',
              {
                customData: {
                  onboarding_packet_id: this.$store.state.onboardingPacket?.id,
                  message: res?.data?.message,
                  res: res,
                  data: res?.data,
                  status: res?.status,
                },
              }
            );
            return this.$router.push({ name: 'CarrierWelcomePage' });
          }
        case 423:
          // This happens when the carrier is not allowed to proceed with the packet
          // because they are set to one of the following error codes
          // 1000 -> The broker has them on do not dispatch
          // 1001 -> The broker only allows carrier to onboard that have been invited and they are not
          // 1002 -> The carrier has hit the daily rate limit.  They must connect their ELD to proceed

          var errorCode = res.data.error_code;
          console.log(
            `423 response creating onboarding packet with error code ${errorCode}`
          );
          // remove the redirect URL because they are not going to be
          // able to proceed
          localStorage.removeItem('redirect_url_after_sigin');
          var routeName;
          switch (errorCode) {
            case 1000:
              routeName = 'Carrier Do Not Dispatch Holding Tank';
              break;
            case 1001:
              routeName = 'CarrierInviteOnly';
              break;
            case 1002:
              routeName = 'CarrierRateLimitExceeded';
              break;
            default:
              // For backwards compatibility
              routeName = 'Carrier Do Not Dispatch Holding Tank';
            // throw (
            //   res.status.toString() +
            //   " - Unsupported error code from server"
            // );
          }
          return this.$router.push({
            name: routeName,
          });
        case 424:
          // This happens when the carrier fails the rules assessment
          // after already being onboarded with a broker
          console.log(
            '424 response creating onboarding packet; Failed Rules Assessment post Onboarding'
          );
          // remove the redirect URL because they are not going to be
          // able to proceed
          localStorage.removeItem('redirect_url_after_sigin');
          this.$store.commit(
            'carriers/setFailedRulesAssessmentData',
            res?.data
          );
          console.log('setFailedRulesAssessmentData');
          console.dir(res?.data);
          return this.$router.push({
            name: 'Carrier Rules Assessment Status',
          });
        case 400:
          // this happens when the Oauth link is malformed
          console.log('400 response creating onboarding packet');
          // remove the redirect URL because they are not going to be
          // able to proceed
          localStorage.removeItem('redirect_url_after_sigin');
          this.errorType = res.data.error;
          this.errorMessage = res.data.error_description;
          // TODO: WE NEED TO HANDLE THIS CASE BY SHOWING THE USER SOME UI;
          // YOU CAN TEST BY ADDING A BAD PARAM IN THE OAUTH2 LINK ON THE LOGIN
          // BUTTON
          // EXAMPLE LINK: https://staging.highway.com/onboarding/oauth/authorizations?response_type=code&client_id=3542d087-57d8-44a8-aefb-c581b8e86a08&scope=all&redirect_uri=https%3A%2F%2Fstaging-login-screens.s3.amazonaws.com%2Fportals%2Farrive.html
          break;
        default:
          // remove the redirect URL because they are not going to be
          // able to proceed.
          localStorage.removeItem('redirect_url_after_sigin');
          throw (
            res.status.toString() +
            ' - Response from oauth/authorizations not supported'
          );
      }
    },
  },
};
</script>
